import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import Icon from '../../Img/Icon';
import DebouncedFilterInput from '../../FormElementsStandalone/DebouncedFilterInput';
import useIsMobile from '../../hooks/useIsMobile';
import HubError from '../../Profile/SavedListsTabContent/HubError';
import EditListNameModal from '../EditListNameModal';
import PlayersList from './PlayersList';
import { getSavedPlayerListDetail, markSavedListViewed } from '../../../actions/saved-lists-actions';
import { showPopupNew } from '../../../actions/popup-actions';
import { PopupTypes } from '../../../utils/constants';
import { pluralize } from '../../../utils/formatting';

import styles from './listDetail.scss';

// selfrate emits lowercase data-testid; the ticket pins this exact prefix as the contract the web
// verifier's Playwright selectors drive against (`list-detail.*`). Rendered once (not in a list), so
// the literal prefix is safe — same posture as the sibling CreateListModal / AddPlayerToListModal.
const TESTID_PREFIX = 'list-detail';

const BACK_LABEL = 'Back to My Saved Lists';
const ADD_PLAYER_LABEL = 'Add Player';
// The exact Figma edit glyph (16×16). Icon renders it as a currentColor mask, so `.editName`'s blue
// tints it — the asset's own fill is irrelevant. Same pencil path the design specifies.
const EDIT_ICON = '/img/v3/icons/icon-16px-edit-blue.svg';
const EDIT_ICON_SIZE = 16;
const LEGEND_LABEL = 'Recent Activity';
const CADENCE_NOTE = 'Ratings update weekly on Mondays';
const SEARCH_PLACEHOLDER = 'Search Players';
const LOAD_ERROR_MESSAGE = 'We couldn\'t load this list. Please try again.';

const normalizeDetail = (data = {}) => ({
    name: data.name ?? '',
    playerCount: data.playerCount ?? 0,
    isDefault: !!data.isDefault,
    lastViewedAt: data.lastViewedAt ?? null,
});

// 404/403 means the list is definitively gone (deleted, not owned, or an invalid id in the URL) — the
// tab falls back to the Hub and strips listid. Any other failure (5xx / network) is transient, so the
// in-place error+retry stays and the deep link is preserved for a refresh to re-attempt.
const isDefinitiveNotAvailable = (error) => {
    const status = error?.response?.status;
    return status === 404 || status === 403;
};

const ListDetail = ({ listId, onBack, onNotAvailable }) => {
    const dispatch = useDispatch();
    const { isMobileWidth } = useIsMobile();

    const [detail, setDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isEditOpen, setIsEditOpen] = useState(false);
    // Bumped when a mutation the container owns (Add Player) needs the players table reloaded in place;
    // PlayersList refetches its feed on the change. The removal flow doesn't use this — it refetches
    // its own feed directly — so this is Add Player's equivalent of that in-place reload.
    const [feedRefreshToken, setFeedRefreshToken] = useState(0);
    // Add Player + Search stay disabled until BOTH the header detail call and the first players-feed
    // call resolve 200 OK. The header half is `!isLoading`; the feed half is reported up here by
    // PlayersList via onFirstLoadComplete. A new list re-arms the gate until its feed reloads.
    const [feedFirstLoadDone, setFeedFirstLoadDone] = useState(false);

    // Monotonic token so only the newest header request applies its response — a refetch triggered by
    // Add Player can otherwise let a slower earlier request resolve last and paint a stale count.
    const requestIdRef = useRef(0);
    // Clicking Back unmounts this view; without this guard an in-flight header request would setState
    // on an unmounted node afterward (the sibling useSavedPlayerLists hook guards the same way).
    const isMountedRef = useRef(true);
    // Mark-viewed is tied to the first feed load and lives on the container, not PlayersList, so it
    // survives a list switch — the only thing that still remounts PlayersList — which re-arms this ref
    // via the [listId] effect below. Background header refreshes (remove / Add Player) no longer remount
    // PlayersList, so within a visit onFirstLoadComplete already fires once; this ref stays as defense.
    const hasMarkedViewedRef = useRef(false);

    const fetchDetail = useCallback((options) => {
        // A background refresh (e.g. after a row removal) keeps the current header on screen and updates
        // it in place when the response lands — no skeleton flash for content the user didn't change.
        const { background = false } = options || {};
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        if (!background) {
            setIsLoading(true);
        }
        setIsError(false);
        return dispatch(getSavedPlayerListDetail(listId))
            .then((result) => {
                if (!isMountedRef.current || requestId !== requestIdRef.current) {
                    return;
                }
                setDetail(normalizeDetail(result?.data));
                setIsLoading(false);
            })
            .catch((error) => {
                if (!isMountedRef.current || requestId !== requestIdRef.current) {
                    return;
                }
                // A definitive not-available (404/403) on a foreground load hands control back to the
                // tab, which drops to the Hub and strips listid — no in-place error page. A background
                // refresh keeps the rendered header (its mutation already succeeded and toasted), so it
                // never triggers the fallback.
                if (!background && isDefinitiveNotAvailable(error) && onNotAvailable) {
                    onNotAvailable();
                    return;
                }
                // A background refresh shouldn't tear the rendered header down to the error state — the
                // mutation that triggered it already succeeded and toasted.
                if (!background) {
                    setIsError(true);
                }
                setIsLoading(false);
            });
    }, [dispatch, listId, onNotAvailable]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    useEffect(() => {
        setFeedFirstLoadDone(false);
        hasMarkedViewedRef.current = false;
    }, [listId]);

    const handleFeedFirstLoad = useCallback(() => {
        setFeedFirstLoadDone(true);
        if (!hasMarkedViewedRef.current) {
            hasMarkedViewedRef.current = true;
            // Best-effort: advances the stored LastViewedAt for the next visit + the hub dot. A failed
            // write shouldn't surface to the user, so swallow it rather than reject unhandled.
            dispatch(markSavedListViewed(listId)).catch(() => {});
        }
    }, [dispatch, listId]);

    const handleAddPlayer = () => {
        dispatch(showPopupNew({
            popup: PopupTypes.ADD_PLAYER_TO_LIST_POPUP,
            savedListIds: [listId],
            // From List Detail the target list is already known, so the modal opens single-step (search +
            // Save straight into this list) instead of the hub's two-step find-then-pick wizard. An
            // explicit flag — not inferred from savedListIds — so a future hub caller that pre-scopes a
            // list can't accidentally collapse to one step.
            singleStep: true,
            // Refresh the count without the header skeleton, and bump the feed token so the newly added
            // player is pulled into the table in place — the background header refresh no longer
            // remounts PlayersList, so nothing else would.
            onSuccess: () => {
                fetchDetail({ background: true });
                setFeedRefreshToken(token => token + 1);
            },
        }));
    };

    const handleRenamed = (newName) => {
        setDetail((prev) => (prev ? { ...prev, name: newName } : prev));
    };

    const canRename = !!detail && !detail.isDefault;

    const renderAddPlayer = (disabled = false) => (
        <button
            type="button"
            className={styles.addPlayer}
            onClick={handleAddPlayer}
            disabled={disabled}
            data-testid={`${TESTID_PREFIX}.add-player`}
        >
            <i className="far fa-plus-circle" />
            <span data-testid={`${TESTID_PREFIX}.add-player.label`}>{ADD_PLAYER_LABEL}</span>
        </button>
    );

    // The legend, cadence note, and their dividers are static chrome shared verbatim by the loaded and
    // loading header so the two states line up while only the dynamic count swaps to a skeleton.
    const renderSummaryChrome = () => (
        <>
            <span className={styles.summaryDivider} />
            <span className={styles.legend} data-testid={`${TESTID_PREFIX}.activity-legend`}>
                <span className={styles.legendDot} />
                <span className={styles.legendLabel} data-testid={`${TESTID_PREFIX}.activity-legend.label`}>
                    {LEGEND_LABEL}
                </span>
            </span>
            <span className={styles.summaryDivider} />
            <span className={styles.cadence} data-testid={`${TESTID_PREFIX}.cadence-note`}>
                {CADENCE_NOTE}
            </span>
        </>
    );

    const renderSearch = (disabled = false) => (
        <div className={`${styles.search} ${disabled ? styles.searchDisabled : ''}`}>
            <DebouncedFilterInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={SEARCH_PLACEHOLDER}
                className={styles.searchInput}
                disabled={disabled}
                data-testid={`${TESTID_PREFIX}.search`}
            />
        </div>
    );

    const renderLoading = () => (
        <div className={styles.loading} data-testid={`${TESTID_PREFIX}.loading`}>
            <div className={styles.titleRow}>
                <div className={styles.titleGroup}>
                    <span
                        className={`${styles.skeleton} ${styles.skeletonTitle}`}
                        data-testid={`${TESTID_PREFIX}.loading.title`}
                    />
                </div>
                {renderAddPlayer(true)}
            </div>
            {isMobileWidth ? (
                // Mirror the loaded mobile header (search only) so the layout doesn't shift when detail
                // resolves; the count/legend live in PlayersList's mobile header there.
                renderSearch(true)
            ) : (
                <div className={styles.metaRow}>
                    <div className={styles.summary}>
                        <span
                            className={`${styles.skeleton} ${styles.skeletonCount}`}
                            data-testid={`${TESTID_PREFIX}.loading.player-count`}
                        />
                        {renderSummaryChrome()}
                    </div>
                    {renderSearch(true)}
                </div>
            )}
        </div>
    );

    const renderHeader = () => (
        <>
            <div className={styles.titleRow}>
                <div className={styles.titleGroup}>
                    <h2 className={styles.title} data-testid={`${TESTID_PREFIX}.title`}>
                        {detail.name}
                    </h2>
                    {canRename && (
                        <button
                            type="button"
                            className={styles.editName}
                            onClick={() => setIsEditOpen(true)}
                            aria-label="Edit list name"
                            data-testid={`${TESTID_PREFIX}.edit-name`}
                        >
                            <Icon src={EDIT_ICON} size={EDIT_ICON_SIZE} />
                        </button>
                    )}
                </div>
                {renderAddPlayer(!feedFirstLoadDone)}
            </div>
            {isMobileWidth ? (
                // Mobile (101-7453) puts the search on its own row; the count, activity legend, sort, and
                // Expand All controls render inside PlayersList's mobile header instead of here.
                renderSearch(!feedFirstLoadDone)
            ) : (
                <div className={styles.metaRow}>
                    <div className={styles.summary}>
                        <span className={styles.count} data-testid={`${TESTID_PREFIX}.player-count`}>
                            {`Showing ${detail.playerCount} ${pluralize('Player', detail.playerCount)}`}
                        </span>
                        {renderSummaryChrome()}
                    </div>
                    {renderSearch(!feedFirstLoadDone)}
                </div>
            )}
            <PlayersList
                listId={listId}
                viewedAsOf={detail.lastViewedAt}
                searchQuery={searchQuery}
                playerCount={detail.playerCount}
                onFirstLoadComplete={handleFeedFirstLoad}
                onPlayerRemoved={() => fetchDetail({ background: true })}
                feedRefreshToken={feedRefreshToken}
                data-testid={`${TESTID_PREFIX}.players`}
            />
        </>
    );

    const renderBody = () => {
        if (isLoading) {
            return renderLoading();
        }
        if (isError || !detail) {
            return (
                <HubError
                    data-testid={`${TESTID_PREFIX}.error`}
                    message={LOAD_ERROR_MESSAGE}
                    onRetry={fetchDetail}
                />
            );
        }
        return renderHeader();
    };

    return (
        <div className={styles.listDetail} data-testid={TESTID_PREFIX}>
            <button
                type="button"
                className={styles.back}
                onClick={onBack}
                data-testid={`${TESTID_PREFIX}.back`}
            >
                <i className="fas fa-chevron-left" />
                <span data-testid={`${TESTID_PREFIX}.back.label`}>{BACK_LABEL}</span>
            </button>
            {renderBody()}
            {isEditOpen && detail && (
                <EditListNameModal
                    listId={listId}
                    currentName={detail.name}
                    onCloseClick={() => setIsEditOpen(false)}
                    onRenamed={handleRenamed}
                />
            )}
        </div>
    );
};

ListDetail.propTypes = {
    listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onBack: PropTypes.func.isRequired,
    // Called on a definitive not-available (404/403) foreground load so the tab can fall back to the
    // Hub and strip listid. Optional: a caller that renders ListDetail outside the deep-link tab keeps
    // the default in-place error+retry.
    onNotAvailable: PropTypes.func,
};

ListDetail.defaultProps = {
    onNotAvailable: null,
};

export default ListDetail;
